import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Phone, ArrowRight, Lock, AlertCircle, KeyRound, MessageCircle, CheckCircle, ExternalLink } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const LoginPage = ({ onLogin }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const telegramLink = searchParams.get('telegram_link') || '';
  const packageName = searchParams.get('package') || '';
  const justPaid = searchParams.get('paid') === 'true';

  const [mode, setMode] = useState('password'); // 'password' | 'otp'
  const [step, setStep] = useState('phone');     // otp flow: 'phone' | 'otp' | 'setpassword'
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [sessionToken, setSessionToken] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const persistAndFinish = (data) => {
    localStorage.setItem('subscriber_token', data.token);
    localStorage.setItem('subscriber_name', data.user_name || '');
    localStorage.setItem('subscriber_phone', data.phone);
    if (onLogin) onLogin(data);
    navigate('/dashboard');
  };

  const switchMode = (m) => {
    setMode(m);
    setStep('phone');
    setError('');
    setMessage('');
    setOtp('');
    setPassword('');
  };

  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      const res = await fetch(`${API_URL}/api/auth/login-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: phone.trim(), password }),
      });
      const data = await res.json();
      if (res.ok) {
        persistAndFinish(data);
      } else {
        setError(data.detail || 'Login failed');
      }
    } catch (_e) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSendOTP = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setLoading(true); setError('');
    try {
      const res = await fetch(`${API_URL}/api/auth/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: phone.trim() }),
      });
      const data = await res.json();
      if (res.ok) { setMessage(data.message); setStep('otp'); }
      else setError(data.detail || 'Failed to send OTP');
    } catch (_e) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      const res = await fetch(`${API_URL}/api/auth/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: phone.trim(), otp_code: otp.trim() }),
      });
      const data = await res.json();
      if (res.ok) {
        if (data.has_password) {
          persistAndFinish(data);
        } else {
          // First-time login — prompt to set a password.
          setSessionToken(data.token);
          localStorage.setItem('subscriber_token', data.token);
          localStorage.setItem('subscriber_name', data.user_name || '');
          localStorage.setItem('subscriber_phone', data.phone);
          setMessage('');
          setStep('setpassword');
        }
      } else {
        setError(data.detail || 'Invalid OTP');
      }
    } catch (_e) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSetPassword = async (e) => {
    e.preventDefault();
    setError('');
    if (newPassword.length < 6) { setError('Password must be at least 6 characters.'); return; }
    if (newPassword !== confirmPassword) { setError('Passwords do not match.'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/set-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${sessionToken}` },
        body: JSON.stringify({ password: newPassword }),
      });
      const data = await res.json();
      if (res.ok) {
        navigate('/dashboard');
      } else {
        setError(data.detail || 'Failed to set password');
      }
    } catch (_e) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => { setOtp(''); await handleSendOTP(); };

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-gray-900 to-black flex items-center justify-center p-4">
      <div className="w-full max-w-md space-y-4">
        {/* Payment success + Telegram join banner */}
        {(justPaid || telegramLink) && (
          <Card className="bg-gradient-to-r from-blue-900/60 to-blue-800/50 border-blue-500/50" data-testid="payment-telegram-banner">
            <CardContent className="p-5 text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-green-500 rounded-full mb-3">
                <CheckCircle className="w-7 h-7 text-white" />
              </div>
              <h2 className="text-lg font-bold text-white mb-1">Payment Successful!</h2>
              <p className="text-sm text-blue-200 mb-4">
                {packageName ? `Your ${packageName} subscription is active.` : 'Your subscription is active.'} Join your Telegram channel below, then log in.
              </p>
              {telegramLink && (
                <a
                  href={telegramLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 bg-blue-500 hover:bg-blue-600 text-white font-semibold py-3 px-6 rounded-lg transition-all transform hover:scale-105 shadow-lg"
                  data-testid="join-telegram-btn"
                >
                  <MessageCircle className="w-5 h-5" />
                  Join your Telegram Channel
                  <ExternalLink className="w-4 h-4" />
                </a>
              )}
            </CardContent>
          </Card>
        )}

        <Card className="bg-gray-900 border-golden/30">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <img
                src="https://customer-assets.emergentagent.com/job_invest-links/artifacts/kydgz8tm_WhatsApp%20Image%202025-08-23%20at%203.47.06%20PM.jpeg"
                alt="Upper Circuit Stocks Logo"
                className="h-16 w-auto"
              />
            </div>
            <CardTitle className="text-2xl text-golden">Subscriber Login</CardTitle>
            <CardDescription className="text-gray-400">
              {step === 'setpassword'
                ? 'Set a password so you can log in faster next time'
                : mode === 'password'
                  ? 'Login with your phone number and password'
                  : step === 'phone' ? 'Enter your registered phone number' : 'Enter the OTP sent to your email'}
            </CardDescription>
          </CardHeader>

          <CardContent>
            {/* Mode tabs (hidden during set-password) */}
            {step !== 'setpassword' && (
              <div className="flex mb-5 bg-gray-800 rounded-lg p-1" data-testid="login-mode-tabs">
                <button
                  type="button"
                  onClick={() => switchMode('password')}
                  className={`flex-1 py-2 rounded-md text-sm font-medium transition-colors ${mode === 'password' ? 'bg-golden text-black' : 'text-gray-400 hover:text-white'}`}
                  data-testid="tab-password"
                >
                  Password
                </button>
                <button
                  type="button"
                  onClick={() => switchMode('otp')}
                  className={`flex-1 py-2 rounded-md text-sm font-medium transition-colors ${mode === 'otp' ? 'bg-golden text-black' : 'text-gray-400 hover:text-white'}`}
                  data-testid="tab-otp"
                >
                  OTP
                </button>
              </div>
            )}

            {error && (
              <div className="mb-4 p-3 bg-red-900/30 border border-red-500/50 rounded-lg flex items-center gap-2 text-red-400" data-testid="login-error">
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                <span className="text-sm">{error}</span>
              </div>
            )}
            {message && step === 'otp' && (
              <div className="mb-4 p-3 bg-green-900/30 border border-green-500/50 rounded-lg text-green-400 text-sm">
                {message}
              </div>
            )}

            {/* PASSWORD LOGIN */}
            {step !== 'setpassword' && mode === 'password' && (
              <form onSubmit={handlePasswordLogin} className="space-y-4" data-testid="password-login-form">
                <div className="space-y-2">
                  <label className="text-sm text-gray-300">Phone Number</label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 w-5 h-5" />
                    <Input type="tel" placeholder="Enter your phone number" value={phone}
                      onChange={(e) => setPhone(e.target.value)} required
                      className="pl-10 bg-gray-800 border-gray-700 text-white placeholder-gray-500"
                      data-testid="password-phone-input" />
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-sm text-gray-300">Password</label>
                  <div className="relative">
                    <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 w-5 h-5" />
                    <Input type="password" placeholder="Enter your password" value={password}
                      onChange={(e) => setPassword(e.target.value)} required
                      className="pl-10 bg-gray-800 border-gray-700 text-white placeholder-gray-500"
                      data-testid="password-input" />
                  </div>
                </div>
                <Button type="submit" disabled={loading || !phone.trim() || !password}
                  className="w-full bg-golden hover:bg-golden/90 text-black font-semibold" data-testid="password-login-btn">
                  {loading ? 'Logging in...' : 'Login'}
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
                <p className="text-xs text-center text-gray-500">
                  First time here? Use the <button type="button" onClick={() => switchMode('otp')} className="text-golden hover:underline">OTP tab</button> to log in and set a password.
                </p>
              </form>
            )}

            {/* OTP — enter phone */}
            {step === 'phone' && mode === 'otp' && (
              <form onSubmit={handleSendOTP} className="space-y-4" data-testid="otp-phone-form">
                <div className="space-y-2">
                  <label className="text-sm text-gray-300">Phone Number</label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 w-5 h-5" />
                    <Input type="tel" placeholder="Enter your phone number" value={phone}
                      onChange={(e) => setPhone(e.target.value)} required
                      className="pl-10 bg-gray-800 border-gray-700 text-white placeholder-gray-500"
                      data-testid="otp-phone-input" />
                  </div>
                  <p className="text-xs text-gray-500">Use the phone number you registered with</p>
                </div>
                <Button type="submit" disabled={loading || !phone.trim()}
                  className="w-full bg-golden hover:bg-golden/90 text-black font-semibold" data-testid="send-otp-btn">
                  {loading ? 'Sending OTP...' : 'Send OTP'}
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </form>
            )}

            {/* OTP — enter code */}
            {step === 'otp' && mode === 'otp' && (
              <form onSubmit={handleVerifyOTP} className="space-y-4" data-testid="otp-verify-form">
                <div className="space-y-2">
                  <label className="text-sm text-gray-300">Enter OTP</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 w-5 h-5" />
                    <Input type="text" placeholder="Enter 6-digit OTP" value={otp}
                      onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))} maxLength={6} required
                      className="pl-10 bg-gray-800 border-gray-700 text-white placeholder-gray-500 text-center text-xl tracking-widest"
                      data-testid="otp-input" />
                  </div>
                </div>
                <Button type="submit" disabled={loading || otp.length !== 6}
                  className="w-full bg-golden hover:bg-golden/90 text-black font-semibold" data-testid="verify-otp-btn">
                  {loading ? 'Verifying...' : 'Verify & Login'}
                </Button>
                <div className="flex justify-between items-center text-sm">
                  <button type="button" onClick={() => { setStep('phone'); setOtp(''); setError(''); }} className="text-gray-400 hover:text-golden">Change Phone Number</button>
                  <button type="button" onClick={handleResendOTP} className="text-golden hover:text-golden/80" disabled={loading}>Resend OTP</button>
                </div>
              </form>
            )}

            {/* SET PASSWORD (first login) */}
            {step === 'setpassword' && (
              <form onSubmit={handleSetPassword} className="space-y-4" data-testid="set-password-form">
                <div className="space-y-2">
                  <label className="text-sm text-gray-300">Create Password</label>
                  <div className="relative">
                    <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 w-5 h-5" />
                    <Input type="password" placeholder="At least 6 characters" value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)} required
                      className="pl-10 bg-gray-800 border-gray-700 text-white placeholder-gray-500"
                      data-testid="new-password-input" />
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-sm text-gray-300">Confirm Password</label>
                  <div className="relative">
                    <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 w-5 h-5" />
                    <Input type="password" placeholder="Re-enter password" value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)} required
                      className="pl-10 bg-gray-800 border-gray-700 text-white placeholder-gray-500"
                      data-testid="confirm-password-input" />
                  </div>
                </div>
                <Button type="submit" disabled={loading || !newPassword || !confirmPassword}
                  className="w-full bg-golden hover:bg-golden/90 text-black font-semibold" data-testid="save-password-btn">
                  {loading ? 'Saving...' : 'Save Password & Continue'}
                </Button>
                <button type="button" onClick={() => navigate('/dashboard')} className="w-full text-sm text-gray-400 hover:text-golden" data-testid="skip-password-btn">
                  Skip for now
                </button>
              </form>
            )}

            <div className="mt-6 pt-4 border-t border-gray-800 text-center">
              <p className="text-sm text-gray-500">
                Don&apos;t have a subscription?{' '}
                <button onClick={() => navigate('/')} className="text-golden hover:underline">Subscribe Now</button>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default LoginPage;
